import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { 
  TextField, 
  Box,
  Typography,
  Chip,
  Autocomplete
} from '@mui/material';
import { 
  Container, 
  Header, 
  HeaderLeft, 
  Title, 
  Content, 
  BackButton, 
  HeaderButton,
  TabContainer,
  TabButton,
  FormContainer,
  FormSection,
  SectionTitle,
  FormRow
} from '../../../EmployeesRepository/EmployeeForm/styles';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EditableItemList from '../../../MyActivities/MyCV/components/common/EditableItemList';
import ConfirmationModal from '../../../common/ConfirmationModal';
import { useAlert } from '../../../../contexts/AlertContext';
import { COLORS } from '../../../../constants/radar';
import { permissionsApi } from '../../../../services/api/permissionsApi';

const TABS = {
  RESOURCE_DATA: 'RESOURCE_DATA',
  PERMISSIONS: 'PERMISSIONS'
};

const ResourceDetails = () => {
  const navigate = useNavigate();
  const { resourceId } = useParams();
  const { showAlert } = useAlert();
  const [isLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.RESOURCE_DATA);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    publishedAt: null,
    isPublished: false
  });
  const [permissions, setPermissions] = useState([]);
  const [expandedPermissionIndex, setExpandedPermissionIndex] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, permission: null });
  const [availableActions, setAvailableActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(false);
  
  // Use refs to track state without causing re-renders
  const initialPermissionsRef = useRef([]);
  const saveTimeoutRef = useRef(null);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (resourceId) {
      loadResourceData();
    }
  }, [resourceId, navigate, showAlert]);

  const loadResourceData = async () => {
    try {
      const resourceData = await permissionsApi.getResourceById(resourceId);
      setFormData({
        name: resourceData.name,
        description: resourceData.description,
        publishedAt: resourceData.publishedAt,
        isPublished: resourceData.isPublished
      });
      const permissions = resourceData.permissions || [];
      setPermissions(permissions);
      initialPermissionsRef.current = JSON.parse(JSON.stringify(permissions)); // Deep copy for comparison
    } catch (error) {
      console.error('Error loading resource:', error);
      showAlert(error.message || `Resource "${resourceId}" not found`, 'error');
      navigate('/permissions/resources');
    }
  };

  // Load available actions from API
  useEffect(() => {
    loadActions();
  }, []);

  const loadActions = async () => {
    try {
      setActionsLoading(true);
      const actions = await permissionsApi.getAllActions({ published: true });
      setAvailableActions(actions);
    } catch (error) {
      console.error('Error loading actions:', error);
      showAlert('Failed to load actions', 'error');
      setAvailableActions([]);
    } finally {
      setActionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      // Save the resource data
      await permissionsApi.updateResource(resourceId, {
        description: formData.description
      });
      
      showAlert('Resource updated successfully', 'success');
    } catch (error) {
      console.error('Error updating resource:', error);
      showAlert(error.message || 'Failed to update resource', 'error');
    }
  };

  const createNewPermission = () => {
    return {
      id: `temp-${Date.now()}`,
      action: availableActions.length > 0 ? availableActions[0] : null,
      conditions: '',
      reason: '',
      isNew: true
    };
  };

  // Auto-save function that doesn't cause re-renders
  const autoSavePermissions = useCallback(async () => {
    if (isSavingRef.current) return;
    
    isSavingRef.current = true;
    setIsPermissionsLoading(true);

    try {
      for (const permission of permissions) {
        // Handle new permissions
        if (permission.isNew && permission.action) {
          const permissionData = {
            resourceId: resourceId,
            actionId: permission.action.id,
            conditions: permission.conditions,
            reason: permission.reason
          };
          
          const savedPermission = await permissionsApi.createPermission(permissionData);
          
          // Update permissions without triggering re-render of form
          setPermissions(prev => {
            const updated = prev.map(p => 
              p === permission ? { ...savedPermission, isNew: false } : p
            );
            // Update our ref as well
            initialPermissionsRef.current = updated.filter(p => !p.isNew);
            return updated;
          });
          
          showAlert('Permission created successfully', 'success');
          continue;
        }

        // Handle existing permissions updates
        if (!permission.isNew && permission.id && !permission.id.startsWith('temp-')) {
          const originalPermission = initialPermissionsRef.current.find(p => p.id === permission.id);
          
          if (originalPermission) {
            const hasChanged = originalPermission.action?.id !== permission.action?.id || 
                              originalPermission.conditions !== permission.conditions ||
                              originalPermission.reason !== permission.reason;

            if (hasChanged) {
              await permissionsApi.updatePermission(permission.id, {
                actionId: permission.action.id,
                conditions: permission.conditions,
                reason: permission.reason
              });
              
              // Update our ref to reflect the changes
              initialPermissionsRef.current = initialPermissionsRef.current.map(p => 
                p.id === permission.id ? { ...permission } : p
              );
              
              showAlert('Permission updated successfully', 'success');
            }
          }
        }
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      showAlert(error.message || 'Failed to save permissions', 'error');
    } finally {
      isSavingRef.current = false;
      setIsPermissionsLoading(false);
    }
  }, [permissions, resourceId]);

  // Debounced auto-save effect
  useEffect(() => {
    if (!resourceId || permissions.length === 0) return;
    
    // Skip if this is the initial load
    if (initialPermissionsRef.current.length === 0 && permissions.length > 0 && permissions.every(p => !p.isNew)) {
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      autoSavePermissions();
    }, 1000);

    // Cleanup function
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [permissions, autoSavePermissions]);

  const renderPermissionSummary = (permission) => {
    const conditionsText = permission.conditions && permission.conditions.length > 0 
      ? 'Condition set'
      : 'No condition';
    
    // Find the action data to get the color and name
    const actionData = availableActions.find(action => action.id === permission.action.id);
    const actionName = actionData?.name || 'Unknown';
    const actionColor = actionData?.color; 
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            label={actionName}
            variant="outlined"
            size="small"
            sx={{ 
              borderColor: actionColor,
              color: actionColor,
              backgroundColor: 'transparent',
              fontWeight: 500,
              fontSize: '13px',
              height: '24px',
              minWidth: 'fit-content',
              '& .MuiChip-label': {
                padding: '0 8px',
                whiteSpace: 'nowrap'
              }
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {conditionsText}
          </Typography>
        </Box>
        {permission.reason && (
          <Typography variant="body2" color="text.primary" sx={{ fontStyle: 'italic' }}>
            {permission.reason}
          </Typography>
        )}
      </Box>
    );
  };

  const renderPermissionEditForm = (permission, onChange) => {
    // Find the selected action object by ID
    const selectedAction = availableActions.find(action => action.id === permission.action.id) || null;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Autocomplete
          fullWidth
          options={availableActions}
          value={selectedAction}
          onChange={(_, newValue) => {
            onChange({ ...permission, action: newValue });
          }}
          getOptionLabel={(option) => option.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          loading={actionsLoading}
          disabled={actionsLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Action"
              placeholder="Search and select an action..."
              helperText={actionsLoading ? "Loading available actions from API..." : "Start typing to search for actions"}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            const actionColor = option.color;
            
            return (
              <Box
                component="li"
                key={key || option.name}
                {...otherProps}
                sx={{ 
                  display: 'flex !important', 
                  flexDirection: 'column !important', 
                  alignItems: 'flex-start !important',
                  justifyContent: 'flex-start !important',
                  textAlign: 'left !important',
                  width: '100%',
                  py: 1.5,
                  px: 2,
                  borderBottom: '1px solid #E2E8F0',
                  '&:last-child': {
                    borderBottom: 'none'
                  }
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'flex-start',
                  gap: 1, 
                  mb: 0.5,
                  width: '100%'
                }}>
                  <Chip
                    label={option.name}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      borderColor: actionColor,
                      color: actionColor,
                      backgroundColor: 'transparent',
                      fontWeight: 500,
                      fontSize: '12px',
                      height: '24px',
                      minWidth: 'fit-content',
                      '& .MuiChip-label': {
                        padding: '0 8px',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  />
                </Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    ml: 0.5,
                    lineHeight: 1.3,
                    fontSize: '0.75rem',
                    textAlign: 'left',
                    width: '100%'
                  }}
                >
                  {option.description}
                </Typography>
              </Box>
            );
          }}
          noOptionsText={actionsLoading ? "Loading actions..." : "No actions found"}
          loadingText="Loading actions..."
        />

        <TextField
          label="Conditions (JSON)"
          multiline
          rows={4}
          fullWidth
          value={permission.conditions}
          onChange={(e) => {
            onChange({ ...permission, conditions: e.target.value });
          }}
          placeholder='{ "orgUnitType": { "$eq": "HR" },n  "role": { "$in": ["Manager", "Admin"] } }'
          helperText="Use CASL-style JSON syntax for fine-grained access control"
        />

        <TextField
          label="Reason"
          multiline
          rows={3}
          fullWidth
          value={permission.reason || ''}
          onChange={(e) => onChange({ ...permission, reason: e.target.value })}
          placeholder="Optional human-readable explanation for why this permission exists"
        />
      </Box>
    );
  };

  const handleDeletePermission = (index) => {
    setDeleteModal({ open: true, permission: permissions[index], index });
  };

  const confirmDeletePermission = async () => {
    const { index, permission } = deleteModal;
    
    try {
      setIsPermissionsLoading(true);
      
      // Only call API if the permission exists on the server (has a real ID)
      if (permission.id && !permission.isNew && !permission.id.startsWith('temp-')) {
        await permissionsApi.deletePermission(permission.id);
      }
      
      const newPermissions = [...permissions];
      newPermissions.splice(index, 1);
      setPermissions(newPermissions);
      
      // Adjust expanded index if necessary
      if (expandedPermissionIndex === index) {
        setExpandedPermissionIndex(null);
      } else if (expandedPermissionIndex > index) {
        setExpandedPermissionIndex(expandedPermissionIndex - 1);
      }
      
      setDeleteModal({ open: false, permission: null, index: undefined });
      showAlert('Permission deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting permission:', error);
      showAlert(error.message || 'Failed to delete permission', 'error');
    } finally {
      setIsPermissionsLoading(false);
    }
  };


  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/permissions/resources')}>
            <FaArrowLeft /> Back to Resources
          </BackButton>
          <Title>{formData.name}</Title>
        </HeaderLeft>
        {activeTab === TABS.RESOURCE_DATA && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <HeaderButton onClick={handleSave} sx={{ 
              backgroundColor: COLORS.PRIMARY,
              color: 'white',
              '&:hover': {
                backgroundColor: '#1d4ed8',
              }
            }}>
              Update Resource
            </HeaderButton>
          </Box>
        )}
      </Header>

      <Content>
        <TabContainer>
          <TabButton
            data-active={activeTab === TABS.RESOURCE_DATA}
            onClick={() => setActiveTab(TABS.RESOURCE_DATA)}
          >
            Resource Data
          </TabButton>
          <TabButton
            data-active={activeTab === TABS.PERMISSIONS}
            onClick={() => setActiveTab(TABS.PERMISSIONS)}
          >
            Permissions
          </TabButton>
        </TabContainer>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {activeTab === TABS.RESOURCE_DATA && (
              <FormContainer>
                <FormSection>
                  <SectionTitle>Basic Information</SectionTitle>
                  <FormRow>
                    <TextField
                      label="Name"
                      name="name"
                      required
                      fullWidth
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled // Resource name cannot be changed
                    />
                  </FormRow>
                  <FormRow>
                    <TextField
                      label="Description"
                      name="description"
                      fullWidth
                      multiline
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                    />
                  </FormRow>
                </FormSection>
              </FormContainer>
            )}

            {activeTab === TABS.PERMISSIONS && (
              <FormContainer>
                <FormSection>
                  <SectionTitle>Resource Permissions</SectionTitle>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Define what actions can be performed on this resource and under what conditions.
                  </Typography>
                  
                  <EditableItemList
                    items={permissions}
                    setItems={setPermissions}
                    expandedIndex={expandedPermissionIndex}
                    setExpandedIndex={setExpandedPermissionIndex}
                    createNewItem={createNewPermission}
                    renderSummary={renderPermissionSummary}
                    renderEditForm={renderPermissionEditForm}
                    addButtonText="Add Permission"
                    emptyState={{
                      title: "No permissions defined",
                      description: "Add permissions to define what actions can be performed on this resource."
                    }}
                    itemName="permission"
                    isLoading={isPermissionsLoading}
                    onDelete={handleDeletePermission}
                  />
                </FormSection>
              </FormContainer>
            )}
          </>
        )}
      </Content>

      {/* Delete Permission Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, permission: null, index: undefined })}
        onConfirm={confirmDeletePermission}
        title="Delete Permission"
        message={`Are you sure you want to delete this permission? This action cannot be undone.`}
      />
    </Container>
  );
};

export default ResourceDetails;